import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useSubscriptionFlag } from "../Subscription/flag/subscriptionFlagStore";
import { FREE_USER_HIDDEN_PATHS, isFreeUser } from "../Subscription/flag/flagReasons";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Below this screen width, the sidebar behaves as a mobile drawer
// (closed by default, opens as an overlay).
const RESPONSIVE_BREAKPOINT = 991;

// Roles for which certain sidebar items should be hidden
// (currently used to hide "Junior Leads List").
const RESTRICTED_ROLES = [
  "dsa associates",
  "dsa associate ops",
];

// Roles for which the entire "Invoice" menu item should be hidden,
// along with a handful of other top-level items / sub-items below.
const INVOICE_RESTRICTED_ROLES = [
  "channel business manager",
  "independent business manager",
  "dsa associate ops",
  "dsa associates",
];

// Top-level sidebar items to hide entirely for INVOICE_RESTRICTED_ROLES.
const TOP_LEVEL_INVOICE_RESTRICTED_ITEMS = [
  "Invoice",
  "Send Invitation",
  "Pending DSA Associate List",
  "Manage Junior",
  "Compliance",
  "Assign Leads",
  "Manual GST"
];

// Sub-menu items to hide for INVOICE_RESTRICTED_ROLES (by parent + item name).
const SUBMENU_INVOICE_RESTRICTED_ITEMS = ["Online Wallet"];

// Maps a Channel ID to its corresponding company logo.
const LOGO_MAP = {
  5: "assets/images/CompanyLogo/KSDPL.png",
  6: "assets/images/CompanyLogo/MRBlogo.png",
  1: "assets/images/CompanyLogo/Gullak Logo.png",
  4: "assets/images/CompanyLogo/EternallCompanyLogo.png",
  2: "assets/images/CompanyLogo/SamayCompany.png",
  3: "assets/images/CompanyLogo/Priyal Marketing Logo.png",
};

const Sidebar1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Free User Access employees have no subscription → hide its menu links.
  const { flag: subscriptionFlag } = useSubscriptionFlag();
  const freeUser = isFreeUser(subscriptionFlag);

  // ---------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------

  // Sidebar open/close state. On desktop it starts open; on mobile it starts closed.
  const [sidebar, setSidebar] = useState(window.innerWidth > RESPONSIVE_BREAKPOINT);

  // Tracks which dropdown menu (by index) is currently expanded.
  const [openDropdown, setOpenDropdown] = useState(null);

  // Tracks whether the viewport is currently in "mobile" mode.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= RESPONSIVE_BREAKPOINT);

  // Channel details (used to pick the correct logo for the current channel).
  // Fetched once at login and cached in localStorage (Login.jsx) — read the
  // cache here instead of calling GetChannelById again, since Sidebar
  // remounts on every page (no shared layout wraps the routes).
  const [channelDetails] = useState(() => {
    const cached = localStorage.getItem("ChannelDetails");
    return cached ? JSON.parse(cached) : null;
  });

  const userRole = localStorage.getItem("Roles") || localStorage.getItem("roles");

  // ---------------------------------------------------------------------
  // Responsive handling
  // ---------------------------------------------------------------------

  const handleResize = () => {
    setIsMobile(window.innerWidth <= RESPONSIVE_BREAKPOINT);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ---------------------------------------------------------------------
  // Sidebar open/close handling
  // ---------------------------------------------------------------------

  const handleSidebarToggle = () => {
    // Both branches currently do the same thing, kept separate in case
    // mobile vs desktop toggle behaviour needs to diverge later.
    if (isMobile) {
      setSidebar((prevSidebar) => !prevSidebar);
    } else {
      setSidebar((prevSidebar) => !prevSidebar);
    }
  };

  // Allows other parts of the app to toggle the sidebar via a custom event,
  // e.g. a hamburger button living outside this component.
  useEffect(() => {
    const handlePanelSidebarToggle = () => {
      setSidebar((prevSidebar) => !prevSidebar);
    };

    window.addEventListener("panel-sidebar-toggle", handlePanelSidebarToggle);

    return () => {
      window.removeEventListener("panel-sidebar-toggle", handlePanelSidebarToggle);
    };
  }, []);

  // Adds a body class while the mobile drawer is open (useful for locking
  // background scroll / dimming the page via CSS).
  useEffect(() => {
    document.body.classList.toggle("panel-drawer-open", isMobile && sidebar);

    return () => {
      document.body.classList.remove("panel-drawer-open");
    };
  }, [isMobile, sidebar]);

  // Auto-close the mobile drawer whenever the route changes.
  useEffect(() => {
    if (isMobile) {
      setSidebar(false);
    }
  }, [location.pathname, isMobile]);

  // ---------------------------------------------------------------------
  // Dropdown / active link helpers
  // ---------------------------------------------------------------------

  const toggleDropdown = (index) => {
    setOpenDropdown((prev) => (prev === index ? null : index));
  };

  // const isActive = (link) => {
  //   return window.location.pathname === link;
  // };

  const isActive = (path) => location.pathname === path;

  // ---------------------------------------------------------------------
  // Logout
  // ---------------------------------------------------------------------

  const handleLogOut = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account!",
      icon: "warning",
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonColor: "#32438e",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log me out!",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      localStorage.clear();
      navigate("/");
    }
  };

  // ---------------------------------------------------------------------
  // Sidebar menu configuration
  // ---------------------------------------------------------------------

  const agencySidebar = [
    {
      icon: "bx-user-pin",
      name: "Manage Leads",
      subMenu: [
        { name: "Add Self Lead", link: "/add-leads", icon: "bx-plus-circle" },
        {
          name: "Self Added Lead List",
          link: "/self-added-leads",
          icon: "bx-user-plus",
        },
        { name: "Assigned", link: "/assigned-leads", icon: "bx-refresh" },
        // { name: "Working", link: "/working-leads", icon: "bx-wrench" },
        { name: "Interested", link: "/interested-leads", icon: "bx-heart" },
        { name: "Doable", link: "/doable-leads", icon: "bx-check-circle" },
        {
          name: "Logged In",
          link: "/logged-in-leads",
          icon: "bx-user-check",
        },
        {
          name: "Under Review",
          link: "/under-review-leads",
          icon: "bx-search-alt",
        },
        {
          name: "Sanction",
          link: "/sanction-leads",
          icon: "bx-credit-card",
        },
        { name: "Disbursed", link: "/disbursed-leads", icon: "bx-wallet" },
        {
          name: "Could not Connected",
          link: "/could-not-connected-leads",
          icon: "bx-unlink",
        },
        {
          name: "Delink Pending Request",
          link: "/delinking-Pending-request-list",
          icon: "bx-link-alt",
        },
      ],
    },

    {
      icon: "bx-user-circle",
      name: "Invoice",
      subMenu: [
        {
          icon: "bx-check-shield",
          name: "Tax Invoice : Regular",
          link: "/Business-service-billing",
        },
        {
          icon: "bx-check-shield",
          name: "Tax Invoice : Monthly",
          link: "/month-end-invoice-list",
        },
      ],
    },
    {
      icon: "bx-receipt",
      name: "Compliance",
      subMenu: [
          {
          icon: "bx-mail-send",
          name: "Manual GST TDS Compliance",
          link: "/manual-gst",
        },
        {
          icon: "bx-file",
          name: "GSTR1",
          link: "/gstr1",
        },
        {
          icon: "bx-spreadsheet",
          name: "GSTR-3B",
          link: "/gstr3",
        },
      
        // {
        //   name: "DSA Commission TDS",
        //   link: "/tds",
        //   icon: "bx-shield-quarter",
        // },
        // {
        //   name: "Clawback Recovery",
        //   link: "/clawback",
        //   icon: "bx-revision",
        // },
      ],
    },
    {
      icon: "bx-transfer",
      name: "Manage LEH",
      subMenu: [
        {
          name: "LEH - Loan Exchange Hub",
          link: "/common-leads",
          icon: "bx-refresh",
        },
        {
          name: "Added to bucket",
          link: "/picked-leads",
          icon: "bx-basket",
        },
        {
          icon: "bx bx-credit-card",
          name: "Leader's Of Hub",
          link: "/leader-of-hub",
        },
      ],
    },
    {
      icon: "bx bx-briefcase",
      name: "Payouts & Billing",
      subMenu: [
        {
          name: "Subscription Billing",
          link: "/subscription-billing",
          icon: "bx-briefcase",
        },
        {
          name: "Online Wallet",
          link: "/online-wallet",
          icon: "bx-wallet",
        },
      ],
    },
    {
      icon: "bx-user-voice",
      name: "Manage Junior",
      subMenu: [
        { name: "Junior List", link: "/junior-list", icon: "bx-id-card" },
        {
          name: "Junior Leads List",
          link: "/junior-Leads-list",
          icon: "bx-task",
        },
        { name: "Work Status", link: "/manage-work-status", icon: "bx-task" },
        {
          name: "Former Junior List",
          link: "/former-Junior-list",
          icon: "bx-user-minus",
        },
      ],
    },
    {
      icon: "bx bx-credit-card",
      name: "Pending DSA Associate List",
      link: "/pending-dsa-associate-list",
    },
    {
      icon: "bxs-spreadsheet",
      name: "Assign Leads",
      subMenu: [
        { name: "Upload Leads", link: "/upload-leads", icon: "bx-upload" },
        {
          name: "Un-Assigned (Specific)",
          link: "/manage-un-assigned-lead",
          icon: "bx-user-x",
        },
        {
          name: "Un-Assigned (Common)",
          link: "/manage-un-assigned-lead-common",
          icon: "bx-group",
        },
      ],
    },
    {
      icon: "bx bx-briefcase",
      name: "Send Invitation",
      link: "/Invitation-by-sales-person",
    },

    {
      icon: "bx-bell",
      name: "Manage Ticket",
      subMenu: [
        {
          name: "Raise Ticket",
          link: "/add-ticket",
          icon: "bx-message-square-add",
        },
        { name: "Ticket List", link: "/manage-ticket", icon: "bx-list-ul" },
      ],
    },
  ];

  // ---------------------------------------------------------------------
  // Auto-expand the dropdown that contains the currently active route
  // ---------------------------------------------------------------------

  useEffect(() => {
    const findOpenDropdown = (sidebar) => {
      for (const [index, item] of sidebar.entries()) {
        if (item.subMenu) {
          if (item.subMenu.some((subItem) => isActive(subItem.link))) {
            return index;
          }
        }
      }
      return null;
    };

    const sidebarToCheck = agencySidebar;
    const openKey = findOpenDropdown(sidebarToCheck);
    if (openKey !== null) {
      setOpenDropdown(openKey);
    }
  }, [location]);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  return (
    <>
      {/* Backdrop shown only on mobile while the drawer is open */}
      {isMobile && sidebar && (
        <button
          type="button"
          className="panel-sidebar-backdrop"
          aria-label="Close sidebar"
          onClick={() => setSidebar(false)}
        />
      )}

      <nav
        className={`${sidebar ? "sidebar open" : "sidebar close"} ${
          isMobile ? "sidebar-mobile" : ""
        }`}
      >
        {/* ------------------------------------------------------------ */}
        {/* Header: logo + mobile close button                           */}
        {/* ------------------------------------------------------------ */}
        <header>
          <Link to="/dashboard">
            <div className="image-text">
              <div className="text logo-text">
                <img
                  src={LOGO_MAP[channelDetails?.id] || "assets/images/logo123.png"}
                  alt="Channel Logo"
                  style={{ height: 60 }}
                />
              </div>
            </div>
          </Link>

          <button
            type="button"
            className="panel-sidebar-close"
            aria-label="Close sidebar"
            onClick={handleSidebarToggle}
          >
            <i className="bx bx-x" />
          </button>
        </header>

        {/* ------------------------------------------------------------ */}
        {/* Menu                                                          */}
        {/* ------------------------------------------------------------ */}
        <div className="menu-bar">
          <div className="menu">
            <ul className="menu-links">
              <>
                {/* Main Dashboard Menu Item */}
                <li className="nav-link">
                  <Link
                    className={isActive("/dashboard") ? "active" : ""}
                    to="/dashboard"
                    title="Dashboard"
                  >
                    <i className="bx bx-home-alt icon" />
                    <span className="text nav-text">Dashboard</span>
                  </Link>
                </li>

                {/* Agency Sidebar */}
                {agencySidebar.map((val, index) => {
                  const role = userRole?.trim().toLowerCase();

                  // Hide restricted top-level items entirely for restricted roles.
                  // Covers: Invoice, Send Invitation, Pending DSA Associate List,
                  // Manage Junior, GST.
                  if (
                    TOP_LEVEL_INVOICE_RESTRICTED_ITEMS.includes(val.name) &&
                    INVOICE_RESTRICTED_ROLES.includes(role)
                  ) {
                    return null;
                  }

                  // Check if any submenu item is active (i.e., a subitem's
                  // link matches the current location).
                  const hasActiveSubMenu =
                    val.subMenu && val.subMenu.some((subItem) => isActive(subItem.link));

                  // If the menu item has a submenu, render it as a dropdown.
                  return val.subMenu ? (
                    <li
                      key={index}
                      className={`nav-link dropdown1 ${
                        openDropdown === index || hasActiveSubMenu ? "active" : ""
                      }`}
                      onClick={() => toggleDropdown(index)} // Toggle dropdown on click
                    >
                      <a className="dropdown-toggle1" title={val.name}>
                        <i className={`bx ${val.icon} icon`} />
                        <span className="text nav-text">{val.name}</span>
                        <i
                          className={`arrow bx ${
                            openDropdown === index ? "bx-chevron-up" : "bx-chevron-down"
                          }`}
                        ></i>
                      </a>

                      <ul className={`dropdown-menu1 ${openDropdown === index ? "show" : ""}`}>
                        {val.subMenu.map((item, subIndex) => {
                          const role = userRole?.trim().toLowerCase();

                          // Hide "Junior Leads List" for restricted roles.
                          if (
                            item.name === "Junior Leads List" &&
                            RESTRICTED_ROLES.includes(role)
                          ) {
                            return null;
                          }

                          // Hide "Online Wallet" and "Assigned" (inside
                          // Manage Leads) for INVOICE_RESTRICTED_ROLES.
                          if (
                            SUBMENU_INVOICE_RESTRICTED_ITEMS.includes(item.name) &&
                            INVOICE_RESTRICTED_ROLES.includes(role)
                          ) {
                            return null;
                          }

                          // Hide "Subscription Billing" for free users.
                          if (freeUser && FREE_USER_HIDDEN_PATHS.includes(item.link)) {
                            return null;
                          }

                          return (
                            <li key={subIndex}>
                              <Link
                                className={isActive(item.link) ? "active" : ""}
                                to={item.link}
                                title={item.name}
                              >
                                <i className={`bx ${item.icon} icon`} /> {item.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  ) : (
                    // If no submenu, simply display the menu item.
                    <li className="nav-link" key={index}>
                      <Link
                        className={isActive(val.link) ? "active" : ""}
                        to={val.link}
                        title={val.name}
                      >
                        <i className={`bx ${val.icon} icon`} />
                        <span className="text nav-text">{val.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </>
            </ul>
          </div>

          {/* -------------------------------------------------------- */}
          {/* Bottom: Logout                                            */}
          {/* -------------------------------------------------------- */}
          <div className="bottom-content">
            <li className="nav-link dropdown1" onClick={handleLogOut}>
              <a>
                <i className="bx bx-log-out icon" />
                <span className="text nav-text">Logout</span>
              </a>
            </li>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Sidebar1;